import { Banner, BannerProps } from "@/components/banner";
import React, { createContext, useContext, useState, useCallback } from "react";

type NotificationContextType = {
  showNotification: (props: Omit<BannerProps, "id" | "onClose">) => void;
};

const NotificationContext = createContext<NotificationContextType | undefined>(
  undefined,
);

type Notification = BannerProps & { id: string };

export function NotificationProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const showNotification = useCallback(
    (props: Omit<BannerProps, "id" | "onClose">) => {
      const id = Math.random().toString(36).substr(2, 9);
      setNotifications((current) => [...current, { ...props, id }]);

      if (props.timeout) {
        setTimeout(() => {
          setNotifications((current) => current.filter((n) => n.id !== id));
        }, props.timeout);
      }
    },
    [],
  );

  const handleClose = useCallback((id: string) => {
    setNotifications((current) => current.filter((n) => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ showNotification }}>
      {children}
      <div className="fixed bottom-4 right-4 flex flex-col gap-2 z-50">
        {notifications.map((notification) => (
          <Banner
            key={notification.id}
            {...notification}
            onClose={() => handleClose(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotification must be used within a NotificationProvider",
    );
  }
  return context;
};
